<template>
  <div>
    <div @click.stop="showPopover">
      <slot name="button" :title="activeValuesText" :visible="visible">
        <UiButton
          severity="tertiary"
          class="flex items-center gap-1 whitespace-nowrap !rounded-full !py-[9px] px-5 text-sm"
        >
          {{ activeValuesText }}
          <UiIcon
            name="chevron-down"
            class="size-4 text-icon-tertiary transition-transform"
            :class="{ 'rotate-180': visible }"
          />
        </UiButton>
      </slot>
    </div>
    <UiPopover ref="popover" @open="visible = true" @close="visible = false">
      <div class="py-1.5" :class="containerClasses">
        <slot name="header" />
        <ul class="flex flex-col">
          <li v-for="option in options" :key="option.title" class="py-1 text-sm">
            <UiCheckbox
              :model-value="checkValueIsActive(option)"
              @update:model-value="change(option)"
            >
              <slot name="option" :option="option" :is-active="checkValueIsActive(option)">
                {{ option.title }}
              </slot>
            </UiCheckbox>
          </li>
        </ul>
      </div>
    </UiPopover>
  </div>
</template>

<script setup lang="ts" generic="T extends SelectOption">
import { computed, ref, useTemplateRef } from 'vue'
import UiButton from '../UiButton.vue'
import UiCheckbox from '../UiCheckbox.vue'
import UiIcon from '../UiIcon.vue'
import UiPopover from '../UiPopover.vue'
import type { SelectOption } from './type'

type PropType = {
  modelValue: T[]
  options: T[]
  placeholder?: string
  containerClasses?: string
  visibleLimit?: number
}
type EmitType = {
  (e: 'update:modelValue', value: T[]): void
}

const props = withDefaults(defineProps<PropType>(), {
  placeholder: '',
  containerClasses: '',
  visibleLimit: 2
})
const emit = defineEmits<EmitType>()

const visible = ref(false)
const activeValuesText = computed(() => {
  if (!props.modelValue?.length) return props.placeholder
  let values = props.modelValue.map(({ title }) => title)
  const valuesMoreThanVisibleLimit = values.length > props.visibleLimit

  valuesMoreThanVisibleLimit && (values = [...values.slice(0, props.visibleLimit)])
  const postfix = valuesMoreThanVisibleLimit ? `...` : ''

  return values.join(', ') + postfix
})
const checkValueIsActive = ({ value: optionValue }: T) =>
  !!props.modelValue.find(({ value }) => value === optionValue)

const change = (value: T) => {
  const isActive = checkValueIsActive(value)
  const newValue = isActive
    ? props.modelValue.filter((item) => item.value !== value.value)
    : [...props.modelValue, value]

  emit('update:modelValue', newValue)
}
const popover = useTemplateRef<typeof UiPopover>('popover')

const showPopover = (e: MouseEvent) => {
  popover.value?.toggle(e)
}
</script>
